/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\aiAgentToolsApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


import { api } from '../../../lib/api';

const BASE = '/api/telephony/ai-agents';

export interface AiAgentTool {
    id: number;
    uuid: string;
    domainId: number;
    name: string;
    description: string;
    parameters: Record<string, unknown>;
    endpointUrl: string | null;
    httpMethod: string;
    authType: string;
    authHeader: string;
    authValue: string | null;
    customHeaders: Record<string, string> | null;
    timeoutMs: number;
    enabled: boolean;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface AiAgentToolInput {
    name: string;
    description: string;
    parameters?: Record<string, unknown>;
    endpointUrl?: string | null;
    httpMethod?: string;
    authType?: string;
    authHeader?: string;
    authValue?: string | null;
    customHeaders?: Record<string, string> | null;
    timeoutMs?: number;
    enabled?: boolean;
}

export interface ToolTestResult {
    status: number;
    body: unknown;
    latencyMs: number;
    error?: string;
}

// ---- Tool Library CRUD ----

export async function listTools(params?: { search?: string; page?: number; limit?: number }): Promise<{ data: AiAgentTool[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const resp = await api.get(`${BASE}/tools?${query.toString()}`);
    return resp.data;
}

export async function getToolById(id: number): Promise<AiAgentTool> {
    const resp = await api.get(`${BASE}/tools/${id}`);
    return resp.data.data;
}

export async function createTool(input: AiAgentToolInput): Promise<AiAgentTool> {
    const resp = await api.post(`${BASE}/tools`, input);
    return resp.data.data;
}

export async function updateTool(id: number, input: Partial<AiAgentToolInput>): Promise<AiAgentTool> {
    const resp = await api.put(`${BASE}/tools/${id}`, input);
    return resp.data.data;
}

export async function deleteTool(id: number): Promise<void> {
    await api.delete(`${BASE}/tools/${id}`);
}

export async function testTool(
    id: number, 
    args: Record<string, unknown>,
    authOverride?: { authType?: string; authHeader?: string; authValue?: string | null }
): Promise<ToolTestResult> {
    const payload = authOverride ? { args, ...authOverride } : { args };
    const resp = await api.post(`${BASE}/tools/${id}/test`, payload);
    return resp.data.data;
}

// ---- Per-Agent Assignments ----

export async function getAgentTools(agentId: number | string): Promise<AiAgentTool[]> {
    const resp = await api.get(`${BASE}/${agentId}/tools`);
    return resp.data.data;
}

export async function setAgentTools(agentId: number | string, toolIds: number[]): Promise<void> {
    await api.put(`${BASE}/${agentId}/tools`, { toolIds });
}
